<template>
  <div class="myorders">
    <mainmenu selected="my-orders"></mainmenu>

    <div class="container-fluid myorders-shell">
      <div class="myorders-control-card">
        <div class="myorders-header">
          <div>
            <h1>{{ $t("myorders.page_title") }}</h1>
            <div class="myorders-summary">
              {{ $t("myorders.total_orders") }} <strong>{{ PaginationInfo.RowCount }}</strong>
            </div>
          </div>
        </div>

        <div class="myorders-filters">
          <span class="filters-label">{{ $t("mystocks.filters") }}</span>
          <div class="filters-grid">
            <label class="filter-field">
              <span>{{ $t("myorders.filter_status") }}</span>
              <el-select size="small" class="filter-control" v-model="Filters.order_status">
                <el-option v-for="(s, idx) in Statuses" :key="'status-' + idx" :label="s.name" :value="s.id"></el-option>
              </el-select>
            </label>

            <div class="filter-actions">
              <el-button size="small" type="primary" icon="el-icon-search" @click="applyFilters">{{ $t("filter") }}</el-button>
              <el-button size="small" icon="el-icon-refresh-left" @click="resetFilters">{{ $t("reset") }}</el-button>
            </div>
          </div>
        </div>
      </div>

      <div class="myorders-table-card">
        <div class="table-card-header">
          <div>
            <div class="table-title">{{ $t("myorders.orders") }}</div>
            <div class="table-subtitle">{{ $t("myorders.sorted_by_newest") }}</div>
          </div>
          <span class="table-total">{{ $t("myorders.total") }} {{ PaginationInfo.RowCount }}</span>
        </div>

        <el-table
          class="myorders-table"
          :data="Results"
          :height="tableHeight"
          :row-class-name="orderRowClass"
          stripe
          style="width: 100%"
          v-loading="tableLoadingVisible"
          :empty-text="$t('myorders.empty_results')">
          <el-table-column prop="Id" :label="$t('myorders.unique_id')" width="70"></el-table-column>

          <el-table-column :label="$t('myorders.request_date')" width="165">
            <template slot-scope="scope">
              <div class="date-chip">{{ formatDate(scope.row.RequestDate) }}</div>
            </template>
          </el-table-column>

          <el-table-column prop="FactoryCode" :label="$t('myorders.factory_code')" width="115"></el-table-column>
          <el-table-column prop="SellerName" :label="$t('myorders.seller_name')" width="175" show-overflow-tooltip></el-table-column>

          <el-table-column prop="BuyerFactoryCode" :label="$t('myorders.buyer_factory_code')" width="135"></el-table-column>
          <el-table-column prop="BuyerName" :label="$t('myorders.buyer_name')" width="175" show-overflow-tooltip></el-table-column>

          <el-table-column :label="$t('myorders.article_info')" min-width="350">
            <template slot-scope="scope">
              <div class="article-main">
                <strong>{{ scope.row.ArticleNumber }}</strong>
              </div>
              <div class="article-category">{{ scope.row.ProductCategory }}</div>
              <p class="article-description">{{ scope.row.ProductDescription }}</p>
            </template>
          </el-table-column>

          <el-table-column :label="$t('myorders.order_status')" width="145">
            <template slot-scope="scope">
              <span :class="['status-pill', statusClass(scope.row.RequestStatus)]">{{ statusLabel(scope.row.RequestStatus) }}</span>
              <el-tooltip v-if="scope.row.RequestStatus == 'rejected' && scope.row.RejectReason" :content="scope.row.RejectReason" placement="top">
                <div class="reject-reason">{{ scope.row.RejectReason }}</div>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column prop="Quantity" :label="$t('myorders.quantity')" width="105" align="right">
            <template slot-scope="scope">
              <span class="number-cell">{{ formatNumber(scope.row.Quantity) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('myorders.price')" width="105" align="right">
            <template slot-scope="scope">
              <span class="number-cell">{{ scope.row.Price|format_money }} &euro;</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('myorders.operations')" width="170">
            <template slot-scope="scope">
              <div class="order-actions" v-if="scope.row.RequestStatus == 'pending' && scope.row.ImTheSeller == 1">
                <el-button size="mini" type="success" plain icon="el-icon-check" @click="approve(scope.row)">{{ $t("myorders.approve") }}</el-button>
                <el-button size="mini" type="danger" plain icon="el-icon-close" @click="reject(scope.row)">{{ $t("myorders.reject") }}</el-button>
              </div>

              <a
                v-else-if="scope.row.RequestStatus == 'accepted'"
                :href="scope.row.PdfDownloadUrl"
                target="_blank"
                class="delivery-link">
                <i class="el-icon-download"></i>
                {{ $t("myorders.download_delivery_note") }}
              </a>

              <span v-else class="no-action">{{ $t("myorders.no_action") }}</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          <el-pagination
            background
            @size-change="on_page_size_change"
            @current-change="refresh_info"
            :page-sizes="PaginationInfo.PageSizes"
            :page-size.sync="PaginationInfo.PerPage"
            :current-page.sync="PaginationInfo.Page"
            :total="PaginationInfo.RowCount"
            layout="total, sizes, prev, pager, next">
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";
import { EventBus } from "./../backend/EventBus.js";
import { intlLocaleForLanguage } from "./../utils/languages";

function buildDefaultFilters() {
  return { order_status: "all", order_id: "" };
}

export default {
  name: "my-orders",
  components: {
    "mainmenu": MainMenu
  },
  data () {
    return {
      tableLoadingVisible: false,
      tableHeight: 520,
      Results: [],
      Filters: buildDefaultFilters(),
      Statuses: [
        { id: "all", name: this.$t("all") },
        { id: "pending", name: this.$t("pending") },
        { id: "accepted", name: this.$t("accepted") },
        { id: "rejected", name: this.$t("rejected") }
      ],
      PaginationInfo: { Page: 1, PerPage: 20, RowCount: 0, PageSizes: [20, 50, 100] },
      Categories: [],
      DirectOrderId: ""
    };
  },
  methods: {
    applyDeepLink: function() {
      var factoryId = parseInt(this.$route.query.factory_id);
      if(!isNaN(factoryId) && factoryId > 0) {
        settings.set_active_factory_id(factoryId);
      }

      var orderId = parseInt(this.$route.query.order_id);
      if(!isNaN(orderId) && orderId > 0) {
        this.DirectOrderId = String(orderId);
        this.Filters.order_status = "all";
        this.Filters.order_id = String(orderId);
      }
    },
    updateTableHeight: function() {
      var availableHeight = window.innerHeight - 360;
      this.tableHeight = Math.max(360, Math.min(availableHeight, 680));
    },
    applyFilters: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    resetFilters: function() {
      this.DirectOrderId = "";
      this.Filters = buildDefaultFilters();
      this.applyFilters();
    },
    on_page_size_change: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    refresh_info: function() {
      this.tableLoadingVisible = true;
      this.$http
        .post("myorders/refresh_info", { Filters: this.Filters, PaginationInfo: this.PaginationInfo })
        .then(function(response) {
          this.tableLoadingVisible = false;
          if(settings.verify_response(response, this.$rights)) {
            this.Results = response.body.Results || [];
            this.PaginationInfo = response.body.PaginationInfo;
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.tableLoadingVisible = false;
          this.$message.error(this.$t("myorders.load_failed"));
        });
    },
    approve: async function(item) {
      var confirmed = await this.$confirm(this.$t("myorders.are_you_sure_you_approve"), this.$t("warning_title"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        type: "warning"
      });

      if(confirmed) {
        this.tableLoadingVisible = true;
        var response = await this.$http.post("myorders/aprove", { order_id: item.Id });
        this.tableLoadingVisible = false;

        if(settings.verify_response(response, this.$rights)) {
          this.refresh_info();
        } else {
          this.$router.push("/");
        }
      }
    },
    validate_reason: function(reason) {
      return reason != null && reason.length > 3;
    },
    reject: function(item) {
      this.$prompt(this.$t("myorders.rejection_reason"), this.$t("warning_title"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        inputValidator: this.validate_reason,
        inputErrorMessage: this.$t("myorders.wrong_validate_reason")
      }).then(value => {
        this.tableLoadingVisible = true;
        this.$http
          .post("myorders/reject", { order_id: item.Id, reason: value })
          .then(function(response) {
            this.tableLoadingVisible = false;
            if(settings.verify_response(response, this.$rights)) {
              this.refresh_info();
            } else {
              this.$router.push("/");
            }
          })
          .catch(function() {
            this.tableLoadingVisible = false;
            this.$message.error(this.$t("myorders.load_failed"));
          });
      }).catch(() => {});
    },
    logout: function() {
      settings.logout();
      this.$router.push("/");
    },
    on_factory_context_change: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    formatDate: function(value) {
      if(!value || value === "0000-00-00 00:00:00") return "-";
      var parts = String(value).split(/[- :]/);
      if(parts.length < 5) return value;
      var dateValue = new Date(Date.UTC(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
        Number(parts[3] || 0),
        Number(parts[4] || 0),
        Number(parts[5] || 0)
      ));
      if(isNaN(dateValue.getTime())) return value;
      var pad = function(part) { return String(part).padStart(2, "0"); };
      return pad(dateValue.getDate()) + "." + pad(dateValue.getMonth() + 1) + "." + dateValue.getFullYear() + " " + pad(dateValue.getHours()) + ":" + pad(dateValue.getMinutes());
    },
    formatNumber: function(value) {
      if(value === null || value === undefined || value === "") return "";
      return new Intl.NumberFormat(intlLocaleForLanguage(this.$i18n && this.$i18n.locale)).format(Number(value));
    },
    statusLabel: function(status) {
      if(status === "accepted") return this.$t("accepted");
      if(status === "pending") return this.$t("pending");
      if(status === "rejected") return this.$t("rejected");
      return status || "-";
    },
    statusClass: function(status) {
      if(status === "accepted") return "status-accepted";
      if(status === "pending") return "status-pending";
      if(status === "rejected") return "status-rejected";
      return "status-neutral";
    },
    orderRowClass: function({ row }) {
      return this.DirectOrderId && row && String(row.Id) === String(this.DirectOrderId) ? "direct-order-row" : "";
    }
  },
  created: function() {
    this.applyDeepLink();
  },
  mounted: function() {
    this.updateTableHeight();
    window.addEventListener("resize", this.updateTableHeight);
    this.refresh_info();
    EventBus.$on("on_factory_context_change", this.on_factory_context_change);
  },
  beforeDestroy: function() {
    window.removeEventListener("resize", this.updateTableHeight);
    EventBus.$off("on_factory_context_change", this.on_factory_context_change);
  }
};
</script>

<style lang="less" scoped>
.myorders-shell {
  padding: 14px 10px 28px;
}

.myorders-control-card,
.myorders-table-card {
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 26px rgba(25, 80, 99, 0.13);
}

.myorders-control-card {
  margin-bottom: 14px;
  padding: 16px 18px 15px;
}

.myorders-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 13px;
  border-bottom: 1px solid #e8eef2;

  h1 {
    margin: 0;
    color: #152b38;
    font-size: 31px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 36px;
  }
}

.myorders-summary {
  margin-top: 1px;
  color: rgba(21, 43, 56, 0.72);
  font-size: 13px;
  font-weight: 600;
}

.myorders-filters {
  display: grid;
  grid-template-columns: 54px 1fr;
  align-items: center;
  gap: 14px;
}

.filters-label {
  color: #2e4a5a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.filters-grid {
  display: grid;
  grid-template-columns: minmax(220px, 320px) auto;
  align-items: end;
  gap: 10px;
}

.filter-field {
  display: block;
  margin: 0;
  min-width: 0;

  > span {
    display: block;
    margin-bottom: 5px;
    color: #556b78;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
  }
}

.filter-control {
  width: 100%;

  /deep/ .el-input__inner {
    border-color: #d7e0e7;
    color: #324a59;
    font-weight: 600;
  }
}

.filter-actions,
.order-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.filter-actions {
  padding-bottom: 1px;
  white-space: nowrap;
}

.myorders-table-card {
  overflow: hidden;
  background: #fff;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  padding: 12px 18px;
  border-bottom: 1px solid #e6edf2;
  background: #fbfdfe;
}

.table-title {
  color: #223947;
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
}

.table-subtitle {
  color: #6d7f89;
  font-size: 12px;
  font-weight: 600;
}

.table-total {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  background: #eef6f9;
  color: #2b6275;
  font-size: 12px;
  font-weight: 800;
}

.myorders-table {
  background: #fff;
}

.myorders-table /deep/ th {
  padding: 10px 0;
  background: #f4f8fa;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
}

.myorders-table /deep/ td {
  padding: 8px 0;
  border-bottom-color: #e7edf2;
  color: #405461;
}

.myorders-table /deep/ .cell {
  line-height: 21px;
}

.myorders-table /deep/ .el-table__row:hover > td {
  background: #f4fbfd;
}

.myorders-table /deep/ .direct-order-row > td {
  background: #fff7e6 !important;
}

.date-chip,
.number-cell {
  color: #314d5d;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.article-main {
  color: #314754;
  line-height: 19px;
}

.article-category {
  margin-top: 2px;
  color: #526976;
  font-style: italic;
  line-height: 18px;
}

.article-description {
  display: -webkit-box;
  margin: 4px 0 0;
  overflow: hidden;
  color: #64727c;
  font-size: 12px;
  line-height: 17px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-width: 72px;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
}

.status-accepted {
  background: #eaf6e5;
  color: #3d9b2f;
}

.status-pending {
  background: #fff4e5;
  color: #d5891d;
}

.status-rejected {
  background: #fdeaea;
  color: #c63e3e;
}

.status-neutral {
  background: #eef4f8;
  color: #426274;
}

.reject-reason {
  margin-top: 5px;
  overflow: hidden;
  color: #9b5555;
  font-size: 11px;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delivery-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 112px;
  padding: 6px 10px;
  border: 1px solid #69bd48;
  border-radius: 4px;
  background: #f2fbef;
  color: #45a72e;
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  text-decoration: none;

  i {
    margin-right: 4px;
  }
}

.no-action {
  color: #9aa8af;
  font-size: 12px;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px;
  border-top: 1px solid #e6edf2;
  background: #fbfdfe;
}

@media (max-width: 760px) {
  .myorders-header h1 {
    font-size: 32px;
  }

  .myorders-filters,
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .table-card-header,
  .order-actions {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
